import React, { memo, type PropsWithChildren, useCallback, useState } from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { theme } from '@garnish/constants';
import { Icon, LabelText, useResponsive } from '@garnish/core';

import { CustomizationRestrictionModal } from '../CustomizationModals';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientNotice = memo((props: Props) => {
  const {
    notice,
    ingredientName,
    variation = 'icon',
    restrictionModalTitle,
    restrictionModalButtonTitle,
  } = props;

  const { formatMessage } = useIntl();

  // ─── State ───────────────────────────────────────────────────────────

  const [isModalOpen, setIsModalOpen] = useState(false);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const openModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  // ─────────────────────────────────────────────────────────────────────

  if (variation === 'label') {
    return <CustomizationIngredientNoticeLabel>{notice}</CustomizationIngredientNoticeLabel>;
  }

  return (
    <View>
      <TouchableOpacity
        onPress={openModal}
        accessibilityRole="button"
        accessibilityLabel={formatMessage(messages.toggleLabel, { name: ingredientName })}
        activeOpacity={0.7}
      >
        <Icon name="IconAlert" color={theme.colors.CAUTION} />
      </TouchableOpacity>

      <CustomizationRestrictionModal
        visible={isModalOpen}
        onRequestClose={closeModal}
        accessibilityLabel={formatMessage(messages.modalTitle, { name: ingredientName })}
        restrictionText={notice}
        title={restrictionModalTitle}
        gotItButtonTitle={restrictionModalButtonTitle}
      />
    </View>
  );
});

// ─────────────────────────────────────────────────────────────────────────────

const CustomizationIngredientNoticeLabel = (props: PropsWithChildren) => {
  const { children } = props;

  const { match } = useResponsive();

  const containerStyles = [
    styles.labelContainer,
    match([styles.labelContainerXS, styles.labelContainerSM]),
  ];

  return (
    <View style={containerStyles}>
      <LabelText sizeMatch={['10', '12']} style={styles.text}>
        {children}
      </LabelText>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  labelContainer: {
    backgroundColor: theme.colors.PEACH,
  },
  labelContainerXS: {
    borderRadius: theme.radius.small,
    paddingVertical: 2,
    paddingHorizontal: theme.spacing['1'],
  },
  labelContainerSM: {
    borderRadius: theme.radius.medium,
    paddingVertical: theme.spacing['1'],
    paddingHorizontal: theme.spacing['2'],
  },
  text: {
    textAlign: 'center',
    color: theme.colors.CAUTION,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly modalTitle: { readonly name: MessageValue };
  readonly toggleLabel: { readonly name: MessageValue };
}>({
  modalTitle: {
    defaultMessage: 'Dietary restrictions for {name}',
    description: 'Customization | Ingredient notice | Restriction modal | Title',
  },
  toggleLabel: {
    defaultMessage: 'Open dietary restrictions details for {name}',
    description: 'Customization | Ingredient notice | Toggle label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  ingredientName: string;
  notice: string;
  variation?: 'label' | 'icon';
  restrictionModalTitle?: string;
  restrictionModalButtonTitle?: string;
};
